import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Users, Plus, Settings, Mail, Trash2, UserPlus, Crown, Shield, 
  Eye, Edit, MoreVertical, Check, X, Loader2, Building2, Bell,
  FileText, ChevronRight, Share2, UserMinus, LogOut, Copy, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useTierAccess } from '../hooks/useTierAccess';
import { UpgradePrompt } from '../components/UpgradePrompt';

// Role configuration
const ROLES = {
  owner: { label: 'Owner', icon: Crown, color: 'amber', description: 'Full control' },
  admin: { label: 'Admin', icon: Shield, color: 'indigo', description: 'Manage members & resources' },
  member: { label: 'Member', icon: Users, color: 'emerald', description: 'Create & edit resources' },
  viewer: { label: 'Viewer', icon: Eye, color: 'zinc', description: 'View only' }
};

// Resource types
const RESOURCE_TYPES = {
  watchlist: { label: 'Watchlists', icon: Eye, color: 'amber' },
  alert: { label: 'Monitors', icon: Bell, color: 'rose' },
  report: { label: 'Reports', icon: FileText, color: 'indigo' },
  saved_search: { label: 'Saved Searches', icon: Search, color: 'cyan' }
};

export default function TeamsPage() {
  const { api, user } = useAuth();
  const { canAccess, isAdmin } = useTierAccess();
  const hasAccess = isAdmin || canAccess('teams');
  const [teams, setTeams] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamResources, setTeamResources] = useState([]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Form states
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteMessage, setInviteMessage] = useState('');
  
  // Share resource states
  const [userWatchlists, setUserWatchlists] = useState([]);
  const [userAlerts, setUserAlerts] = useState([]);
  const [userSavedSearches, setUserSavedSearches] = useState([]);
  const [selectedResourceType, setSelectedResourceType] = useState('watchlist');
  const [selectedResourceId, setSelectedResourceId] = useState('');
  
  const [actionLoading, setActionLoading] = useState(false);

  const fetchTeams = async () => {
    try {
      const [teamsRes, invitesRes] = await Promise.all([
        api().get('/teams'),
        api().get('/teams/invitations/pending')
      ]);
      setTeams(teamsRes.data || []);
      setPendingInvites(invitesRes.data || []);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    try {
      const [membersRes, resourcesRes] = await Promise.all([
        api().get(`/teams/${teamId}/members`),
        api().get(`/teams/${teamId}/resources`)
      ]);
      setTeamMembers(membersRes.data || []);
      setTeamResources(resourcesRes.data || []);
    } catch (err) {
      console.error('Failed to fetch team details:', err);
    }
  };

  const fetchUserResources = async () => {
    try {
      const [watchlistsRes, alertsRes, savedSearchesRes] = await Promise.all([
        api().get('/watchlist'),
        api().get('/alerts'),
        api().get('/saved-searches').catch(() => ({ data: [] }))
      ]);
      setUserWatchlists(watchlistsRes.data || []);
      setUserAlerts(alertsRes.data || []);
      setUserSavedSearches(savedSearchesRes.data || []);
    } catch (err) {
      console.error('Failed to fetch user resources:', err);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, [api]);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamDetails(selectedTeam.id);
    }
  }, [selectedTeam]);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await api().post('/teams', {
        name: newTeamName,
        description: newTeamDescription
      });
      
      setTeams([...teams, res.data]);
      setSelectedTeam(res.data);
      setCreateDialogOpen(false);
      setNewTeamName('');
      setNewTeamDescription('');
      toast.success('Team created successfully!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create team';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setActionLoading(true);
    try {
      await api().post(`/teams/${selectedTeam.id}/invite`, {
        email: inviteEmail,
        role: inviteRole,
        message: inviteMessage
      });
      
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteMessage('');
      toast.success(`Invitation sent to ${inviteEmail}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send invitation';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    setActionLoading(true);
    try {
      await api().post(`/teams/invitations/${inviteId}/accept`);
      toast.success('Joined team successfully!');
      fetchTeams();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to accept invitation';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await api().post(`/teams/invitations/${inviteId}/decline`);
      setPendingInvites(pendingInvites.filter(i => i.id !== inviteId));
      toast.success('Invitation declined');
    } catch (err) {
      toast.error('Failed to decline invitation');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    
    try {
      await api().delete(`/teams/${selectedTeam.id}/members/${memberId}`);
      setTeamMembers(teamMembers.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to remove member';
      toast.error(msg);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await api().put(`/teams/${selectedTeam.id}/members/${memberId}/role`, {
        role: newRole
      });
      setTeamMembers(teamMembers.map(m => 
        m.id === memberId ? { ...m, role: newRole } : m
      ));
      toast.success('Role updated');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update role';
      toast.error(msg);
    }
  };

  const handleLeaveTeam = async () => {
    if (!window.confirm('Are you sure you want to leave this team?')) return;
    
    try {
      await api().post(`/teams/${selectedTeam.id}/leave`);
      setTeams(teams.filter(t => t.id !== selectedTeam.id));
      setSelectedTeam(null);
      toast.success('Left team');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to leave team';
      toast.error(msg);
    }
  };

  const handleDeleteTeam = async () => {
    if (!window.confirm('Delete this team? This cannot be undone.')) return;
    
    try {
      await api().delete(`/teams/${selectedTeam.id}`);
      setTeams(teams.filter(t => t.id !== selectedTeam.id));
      setSelectedTeam(null);
      toast.success('Team deleted');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete team';
      toast.error(msg);
    }
  };

  const handleShareResource = async () => {
    if (!selectedResourceId) {
      toast.error('Please select a resource to share');
      return;
    }
    
    setActionLoading(true);
    try {
      await api().post(`/teams/${selectedTeam.id}/share`, {
        resource_type: selectedResourceType,
        resource_id: selectedResourceId,
        team_id: selectedTeam.id
      });
      
      fetchTeamDetails(selectedTeam.id);
      setShareDialogOpen(false);
      setSelectedResourceId('');
      toast.success('Resource shared with team');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to share resource';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnshareResource = async (resourceId) => {
    try {
      await api().delete(`/teams/${selectedTeam.id}/resources/${resourceId}`);
      setTeamResources(teamResources.filter(r => r.id !== resourceId));
      toast.success('Resource unshared');
    } catch (err) {
      toast.error('Failed to unshare resource');
    }
  };

  const openShareDialog = () => {
    fetchUserResources();
    setShareDialogOpen(true);
  };

  const getRoleIcon = (role) => {
    const RoleIcon = ROLES[role]?.icon || Users;
    return RoleIcon;
  };

  const canManageTeam = selectedTeam && ['owner', 'admin'].includes(selectedTeam.my_role);
  const isOwner = selectedTeam?.my_role === 'owner';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="teams-page">
        {!hasAccess ? (
          <div>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <div className="p-2 bg-indigo-500/20 rounded-lg">
                  <Users className="w-6 h-6 text-indigo-400" />
                </div>
                Teams
              </h1>
              <p className="text-zinc-400 mt-1">Collaborate with your organization</p>
            </div>
            <div className="glass-card rounded-xl">
              <UpgradePrompt requiredTier="pro" feature="Team collaboration" />
            </div>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Users className="w-6 h-6 text-indigo-400" />
              </div>
              Teams
            </h1>
            <p className="text-zinc-400 mt-1">Collaborate with your organization on shared watchlists, alerts, and reports</p>
          </div>
          
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="btn-primary"
            data-testid="create-team-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Team
          </Button>
        </div>

        {/* Pending Invitations */}
        {pendingInvites.length > 0 && (
          <div className="glass-card rounded-xl p-4 border-amber-500/30">
            <h3 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Pending Invitations ({pendingInvites.length})
            </h3>
            <div className="space-y-2">
              {pendingInvites.map((invite) => (
                <div key={invite.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div>
                    <p className="text-white font-medium">{invite.team_name}</p>
                    <p className="text-xs text-zinc-500">
                      Invited by {invite.invited_by} as {invite.role}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleAcceptInvite(invite.id)}
                      className="bg-emerald-500 hover:bg-emerald-600"
                      disabled={actionLoading}
                      data-testid={`accept-invite-${invite.id}`}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeclineInvite(invite.id)}
                      className="text-zinc-400 hover:text-rose-400"
                      data-testid={`decline-invite-${invite.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Teams List */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Your Teams</h3>
            
            {teams.length === 0 ? (
              <div className="glass-card rounded-xl p-6 text-center">
                <Users className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm">No teams yet</p>
                <Button 
                  onClick={() => setCreateDialogOpen(true)}
                  className="mt-4 btn-primary"
                >
                  Create Your First Team
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {teams.map((team) => {
                  const RoleIcon = getRoleIcon(team.my_role);
                  return (
                    <button
                      key={team.id}
                      onClick={() => setSelectedTeam(team)}
                      className={`w-full glass-card rounded-xl p-4 text-left transition-all ${
                        selectedTeam?.id === team.id
                          ? 'border-indigo-500/50 bg-indigo-500/10'
                          : 'hover:border-white/20'
                      }`}
                      data-testid={`team-card-${team.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{team.name}</h4>
                          <div className="flex items-center gap-2 text-xs text-zinc-500">
                            <RoleIcon className={`w-3 h-3 text-${ROLES[team.my_role]?.color}-400`} />
                            <span>{ROLES[team.my_role]?.label}</span>
                            <span>•</span>
                            <span>{team.member_count} members</span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-zinc-600" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Team Details */}
          <div className="lg:col-span-2">
            {selectedTeam ? (
              <div className="space-y-6">
                {/* Team Header */}
                <div className="glass-card rounded-xl p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-white">{selectedTeam.name}</h2>
                      <p className="text-zinc-400 text-sm mt-1">{selectedTeam.description || 'No description'}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {selectedTeam.member_count} members
                        </span>
                        <span className="flex items-center gap-1">
                          {(() => {
                            const RoleIcon = getRoleIcon(selectedTeam.my_role);
                            return <RoleIcon className={`w-4 h-4 text-${ROLES[selectedTeam.my_role]?.color}-400`} />;
                          })()}
                          You're {selectedTeam.my_role === 'owner' ? 'the owner' : `a ${selectedTeam.my_role}`}
                        </span>
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-zinc-400">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-white/10">
                        {canManageTeam && (
                          <>
                            <DropdownMenuItem 
                              onClick={() => setInviteDialogOpen(true)}
                              className="text-zinc-300 focus:bg-white/10"
                            >
                              <UserPlus className="w-4 h-4 mr-2" />
                              Invite Member
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setSettingsDialogOpen(true)}
                              className="text-zinc-300 focus:bg-white/10"
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Team Settings
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                          </>
                        )}
                        {!isOwner && (
                          <DropdownMenuItem 
                            onClick={handleLeaveTeam}
                            className="text-rose-400 focus:bg-rose-500/10"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            Leave Team
                          </DropdownMenuItem>
                        )}
                        {isOwner && (
                          <DropdownMenuItem 
                            onClick={handleDeleteTeam}
                            className="text-rose-400 focus:bg-rose-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Team
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  {/* Quick Actions */}
                  {canManageTeam && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                      <Button 
                        onClick={() => setInviteDialogOpen(true)}
                        className="btn-primary"
                        size="sm"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Invite
                      </Button>
                      <Button 
                        onClick={openShareDialog}
                        variant="outline"
                        size="sm"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share Resource
                      </Button>
                    </div>
                  )}
                </div>

                {/* Members */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4">
                    Members ({teamMembers.length})
                  </h3>
                  <div className="space-y-3">
                    {teamMembers.map((member) => {
                      const RoleIcon = getRoleIcon(member.role);
                      const roleConfig = ROLES[member.role];
                      
                      return (
                        <div 
                          key={member.id}
                          className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                          data-testid={`member-${member.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full bg-${roleConfig?.color}-500/20 flex items-center justify-center`}>
                              <RoleIcon className={`w-4 h-4 text-${roleConfig?.color}-400`} />
                            </div>
                            <div>
                              <p className="text-white text-sm font-medium">
                                {member.name}
                                {member.email === user?.email && (
                                  <span className="text-xs text-zinc-500 ml-2">(You)</span>
                                )}
                              </p>
                              <p className="text-xs text-zinc-500">{member.email}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs bg-${roleConfig?.color}-500/20 text-${roleConfig?.color}-400`}>
                              {roleConfig?.label}
                            </span>
                            
                            {canManageTeam && member.role !== 'owner' && member.email !== user?.email && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-zinc-500 hover:text-white h-7 w-7 p-0">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-40 bg-zinc-900 border-white/10">
                                  <DropdownMenuItem 
                                    onClick={() => handleUpdateRole(member.id, 'admin')}
                                    className="text-zinc-300 focus:bg-white/10 text-xs"
                                    disabled={member.role === 'admin'}
                                  >
                                    Make Admin
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleUpdateRole(member.id, 'member')}
                                    className="text-zinc-300 focus:bg-white/10 text-xs"
                                    disabled={member.role === 'member'}
                                  >
                                    Make Member
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleUpdateRole(member.id, 'viewer')}
                                    className="text-zinc-300 focus:bg-white/10 text-xs"
                                    disabled={member.role === 'viewer'}
                                  >
                                    Make Viewer
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-white/10" />
                                  <DropdownMenuItem 
                                    onClick={() => handleRemoveMember(member.id)}
                                    className="text-rose-400 focus:bg-rose-500/10 text-xs"
                                  >
                                    <UserMinus className="w-3 h-3 mr-2" />
                                    Remove
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Shared Resources */}
                <div className="glass-card rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
                      Shared Resources ({teamResources.length})
                    </h3>
                    {['owner', 'admin', 'member'].includes(selectedTeam.my_role) && (
                      <Button 
                        onClick={openShareDialog}
                        variant="ghost"
                        size="sm"
                        className="text-indigo-400 hover:text-indigo-300"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Share
                      </Button>
                    )}
                  </div>
                  
                  {teamResources.length === 0 ? (
                    <div className="text-center py-8">
                      <Share2 className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-500 text-sm">No shared resources yet</p>
                      {['owner', 'admin', 'member'].includes(selectedTeam.my_role) && (
                        <Button 
                          onClick={openShareDialog}
                          variant="ghost"
                          className="mt-2 text-indigo-400"
                        >
                          Share your first resource
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {teamResources.map((resource) => {
                        const typeConfig = RESOURCE_TYPES[resource.resource_type];
                        const TypeIcon = typeConfig?.icon || FileText;
                        
                        return (
                          <div 
                            key={resource.id}
                            className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg bg-${typeConfig?.color}-500/20 flex items-center justify-center`}>
                                <TypeIcon className={`w-4 h-4 text-${typeConfig?.color}-400`} />
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">{resource.resource_name}</p>
                                <p className="text-xs text-zinc-500">
                                  {typeConfig?.label} • Shared by {resource.shared_by}
                                </p>
                              </div>
                            </div>
                            
                            {(resource.shared_by === user?.email || canManageTeam) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnshareResource(resource.id)}
                                className="text-zinc-500 hover:text-rose-400"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="glass-card rounded-xl p-12 text-center">
                <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Select a Team</h3>
                <p className="text-zinc-400 max-w-md mx-auto">
                  Choose a team from the list to view members, shared resources, and manage settings.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Create Team Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="glass-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-indigo-400" />
                Create New Team
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Teams let you collaborate with colleagues on shared watchlists and alerts.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Team Name</Label>
                <Input
                  placeholder="e.g., Property Management Team"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  className="input-dark mt-1"
                  data-testid="team-name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Description (Optional)</Label>
                <Input
                  placeholder="What does this team work on?"
                  value={newTeamDescription}
                  onChange={(e) => setNewTeamDescription(e.target.value)}
                  className="input-dark mt-1"
                  data-testid="team-description-input"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateTeam}
                disabled={actionLoading || !newTeamName.trim()}
                className="btn-primary"
                data-testid="create-team-submit"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Team
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite Member Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent className="glass-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-400" />
                Invite Team Member
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Send an invitation to join {selectedTeam?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Email Address</Label>
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input-dark mt-1"
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-indigo-400" />
                        Admin - Manage members & resources
                      </div>
                    </SelectItem>
                    <SelectItem value="member">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-400" />
                        Member - Create & edit resources
                      </div>
                    </SelectItem>
                    <SelectItem value="viewer">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-zinc-400" />
                        Viewer - View only
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Message (Optional)</Label>
                <Input
                  placeholder="Add a personal message..."
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  className="input-dark mt-1"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setInviteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleInviteMember}
                disabled={actionLoading || !inviteEmail.trim()}
                className="btn-primary"
                data-testid="send-invite-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send Invitation
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Resource Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="glass-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-indigo-400" />
                Share Resource with Team
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Select a watchlist, monitor, or report to share with {selectedTeam?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Resource Type</Label>
                <Select value={selectedResourceType} onValueChange={(v) => {
                  setSelectedResourceType(v);
                  setSelectedResourceId('');
                }}>
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="watchlist">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-amber-400" />
                        Watchlist
                      </div>
                    </SelectItem>
                    <SelectItem value="alert">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-rose-400" />
                        Monitor / Alert
                      </div>
                    </SelectItem>
                    <SelectItem value="saved_search">
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-cyan-400" />
                        Saved Search
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">
                  Select {selectedResourceType === 'watchlist' ? 'Watchlist' : selectedResourceType === 'alert' ? 'Monitor' : 'Saved Search'}
                </Label>
                <Select value={selectedResourceId} onValueChange={setSelectedResourceId}>
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue placeholder="Choose a resource..." />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedResourceType === 'watchlist' && userWatchlists.map((wl) => (
                      <SelectItem key={wl.id} value={wl.id}>
                        {wl.name} ({wl.properties?.length || 0} properties)
                      </SelectItem>
                    ))}
                    {selectedResourceType === 'alert' && userAlerts.map((alert) => (
                      <SelectItem key={alert.id} value={alert.id}>
                        {alert.name}
                      </SelectItem>
                    ))}
                    {selectedResourceType === 'saved_search' && userSavedSearches.map((search) => (
                      <SelectItem key={search.id} value={search.id}>
                        {search.name} ({search.city?.toUpperCase() || 'NYC'})
                      </SelectItem>
                    ))}
                    {selectedResourceType === 'saved_search' && userSavedSearches.length === 0 && (
                      <div className="px-2 py-4 text-sm text-zinc-500 text-center">
                        No saved searches yet. Save a search from the Compliance page to share it.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShareDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleShareResource}
                disabled={actionLoading || !selectedResourceId}
                className="btn-primary"
                data-testid="share-resource-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Share with Team
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Team Settings Dialog */}
        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
          <DialogContent className="glass-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-indigo-400" />
                Team Settings
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <h4 className="text-sm font-medium text-white mb-2">Team Limits</h4>
                <p className="text-xs text-zinc-400">
                  Maximum members: {selectedTeam?.settings?.max_members || 5}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  Based on team owner's subscription tier
                </p>
              </div>
              
              <div className="text-xs text-zinc-500 text-center">
                More settings coming soon...
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => setSettingsDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </>
        )}
      </div>
    </DashboardLayout>
  );
}
